/* ============================================================
   Fast-entry DATE parsing — "Call mom tomorrow 5pm" → a day, a time, and the
   spans of text that said so. The other half of shared/src/capture: `parse.ts`
   reads the sigil'd grouping tokens (`#project`, `@tag`), this reads the
   sigil-LESS date and time words.

   That missing sigil is the whole design problem, and everything below follows
   from it. `#Groceries` can only ever be a project; `monday` might be a due date
   or it might be the name of the task ("Book Monday flights"). So this parser is
   built to be WRONG SAFELY rather than to be clever:

   - **It returns spans, not just values.** The caller highlights exactly what it
     read, in place, so a wrong guess is visible before it is saved rather than
     discovered afterwards in a list. That is the whole reason `spans` exists.
   - **It never guesses across ambiguity.** Every phrase below has exactly one
     defensible reading; anything with two (see "Deliberately not parsed") is
     left alone as ordinary words. A parser that is right 70% of the time on
     `12/25` is worse than one that doesn't try, because the 30% is silent.
   - **It is pure.** `today` is passed in, never read from a clock — same rule as
     recurrence/dates.ts, and what makes the whole vocabulary unit-testable.

   VOCABULARY (Todoist's, minus everything ambiguous):

     today · tonight · tomorrow · tmrw · tmr
     mon…sun, monday…sunday        the NEXT one, strictly after today
     next <weekday>                that one, in next week
     next week                     the first day of next week
     this weekend / weekend        the coming Saturday
     next weekend                  the Saturday of next week
     in 3 days · in 2 weeks · in a week
     aug 9 · august 9 · 9 aug      the next such date, this year or next
     2026-09-01                    ISO, unambiguous by construction

     next month · in 2 months      the same day of the month, clamped

     5pm · 5 pm · 5:30pm · 17:00 · at 5:30 pm · noon

   RECURRENCE — an "every …" phrase sets the Repeat chip AND the day it starts:

     every day · daily             every week · weekly       every month · monthly
     every monday                  weekly on that weekday, anchored on the next one
     every weekday                 Mon–Fri
     every other day/week/month    interval 2
     every 3 days · every 2 weeks · every 6 months

   DELIBERATELY NOT PARSED, each for a stated reason:

   - **`12/25`** — 25 December to a European, and nothing at all on the 25th of
     any month to an American reading it as December 25th vs. 12 May. The app
     ships to both. A numeric date is the one phrase where being wrong looks
     exactly like being right, so it stays text.
   - **A bare hour — "at 5"** — mostly because a lone digit is far more often part
     of the task than a time ("call 5 people", "buy 2 pints"), and a false
     positive there costs the user a word. Requires am/pm or a colon.
     A colon IS accepted without a meridiem, and that is a real (if smaller)
     ambiguity kept on purpose: `5:30` is read as 05:30, so someone who meant the
     evening gets a morning reminder. What makes it survivable is that a clock
     reading is unmistakably a time — the false-positive argument above doesn't
     apply — and the sheet shows the resolved value ("at 5:30 AM") on its receipt
     line before anything is saved. Refusing it would also cost the 24-hour
     forms, which are the whole reason the colon is here.
   - **`midnight`** — tonight's or tomorrow's is a real ambiguity; `noon` has no
     such problem, so it is in.
   - **`every year`** — `RecurrencePreset` has no yearly shape, and approximating
     it as "every 12 months" would be a different rule with the same name.
   - **`yesterday` and past dates in general** — capture is forward-looking; a
     task scheduled into the past would land straight in Overdue.
   - **`@5pm`** — `@` is the TAG prefix (parse.ts). It opens a tag token, and
     stealing it here would break the axis that owns the character.
   ============================================================ */
import { addDaysYMD, utcToYMD, ymdToUTC } from '../recurrence/dates';
import type { RecurrencePreset } from '../recurrence/preset';
import type { Weekday } from '../recurrence/types';

/** Where a parsed phrase sat in the original text, as a half-open span. */
export interface DateSpan {
  start: number;
  end: number;
}

/** What a text resolved to. `day` and `remindAt` are independent: "5pm" carries
    a time and no day, "tomorrow" a day and no time. */
export interface CaptureDate {
  /** "YYYY-MM-DD", or null when only a time was found */
  day: string | null;
  /** minutes since local midnight, or null when only a day was found */
  remindAt: number | null;
  /** the cadence an "every …" phrase asked for, or null. Always paired with a
      `day`: a repeating task is anchored on its own `scheduled_for` (the chain
      model — see docs/recurring-tasks.md), so a cadence with nowhere to start
      is not a thing this can return. */
  recurrence: RecurrencePreset | null;
  /** every span the phrases occupied, in order and non-overlapping — what the
      caller highlights, and what save() cuts out of the name */
  spans: DateSpan[];
  /** the matched text itself, joined — the identity a dismissal is remembered
      by, so clearing the chip on "monday" doesn't also suppress a later
      "tomorrow" (see the sheet's `dismissedPhrase`) */
  phrase: string;
}

/** A week begins on this `Date.getDay()` index (0 = Sunday). Mirrors the app's
    `WeekStart`, redeclared here so shared/ stays free of app imports. */
export type WeekStartDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

/** Weekday names indexed by `getUTCDay()`. Abbreviations are the first three
    letters, which is true for all seven in English and is what "tue"/"thu"
    already look like. */
const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

/** Number words, so "in a week" and "in two days" read the same as digits. Stops
    at twelve: past that people type numerals. */
const NUMBER_WORDS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

/** RRULE weekday codes, indexed like `getUTCDay()`. */
const WEEKDAY_CODE: readonly Weekday[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

/** `getUTCDay()` of a "YYYY-MM-DD". */
function dayOfWeek(ymd: string): number {
  return ymdToUTC(ymd).getUTCDay();
}

/**
 * Shift a day by whole months, CLAMPING to the end of the target month.
 *
 * "in a month" from the 31st lands on the 30th (or the 28th), because the
 * alternative — letting Date roll 31 February into March — silently answers a
 * different question than the one asked. Clamping is what every date library and
 * every calendar app does with a month-relative date.
 */
function addMonthsYMD(ymd: string, months: number): string {
  const base = ymdToUTC(ymd);
  const day = base.getUTCDate();
  const shifted = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + months, 1));
  const lastOfMonth = new Date(
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0),
  ).getUTCDate();
  shifted.setUTCDate(Math.min(day, lastOfMonth));
  return utcToYMD(shifted);
}

/**
 * The next occurrence of `weekday`, STRICTLY after `today`.
 *
 * Strictly: on a Monday, "monday" means the one coming, not the one you are
 * standing in. Someone who meant today would have typed "today" — and the
 * failure modes are not symmetrical, since silently resolving to today drops a
 * task into a day that may be nearly over. Things 3 reads a bare weekday the
 * same way. (Todoist resolves it to today; that is the one place this
 * deliberately diverges from its vocabulary.)
 */
function nextWeekday(today: string, weekday: number): string {
  const delta = (weekday - dayOfWeek(today) + 7) % 7;
  return addDaysYMD(today, delta === 0 ? 7 : delta);
}

/** The first day of the week `today` sits in, per the user's week start. */
function weekStartOf(today: string, weekStartsOn: WeekStartDay): string {
  const back = (dayOfWeek(today) - weekStartsOn + 7) % 7;
  return addDaysYMD(today, -back);
}

/**
 * `next <weekday>` — that weekday in the week AFTER the current one.
 *
 * This is where "next Friday" earns its reputation for ambiguity, so the rule is
 * mechanical and stated in the docs: take the user's own week boundary
 * (Settings → week starts on), step into the following week, and land on the
 * weekday inside it. On a Wednesday with a Monday week start, "next friday" is
 * therefore 9 days out, not 2 — 2 days out is what plain "friday" already means,
 * and a phrase that means the same as its shorter form would be dead vocabulary.
 */
function weekdayInNextWeek(today: string, weekday: number, weekStartsOn: WeekStartDay): string {
  const nextWeek = addDaysYMD(weekStartOf(today, weekStartsOn), 7);
  const delta = (weekday - dayOfWeek(nextWeek) + 7) % 7;
  return addDaysYMD(nextWeek, delta);
}

/**
 * A month/day pair as the next date that matches, this year or next.
 *
 * "aug 9" typed on 20 August means next year's, because capture is
 * forward-looking (the same reason `yesterday` is unparsed): a task scheduled
 * into the past lands straight in Overdue, which is a worse answer than the one
 * the user has to correct by a year.
 *
 * Returns null when the date can't exist (`feb 30`), so nonsense stays text.
 */
function monthDayToYMD(today: string, month: number, date: number): string | null {
  if (date < 1 || date > 31) return null;
  const year = Number(today.slice(0, 4));
  for (const y of [year, year + 1]) {
    const candidate = new Date(Date.UTC(y, month, date));
    // Date rolls Feb 30 into March — comparing back is how we reject it. CONTINUE
    // rather than bail: "feb 29" is impossible in a common year and perfectly
    // fine in the leap year that may be the very next candidate.
    if (candidate.getUTCMonth() !== month || candidate.getUTCDate() !== date) continue;
    const ymd = utcToYMD(candidate);
    if (ymd >= today) return ymd;
  }
  return null;
}

/** Minutes since midnight from a 12/24-hour reading, or null if out of range. */
function toMinutes(hour: number, minute: number, meridiem: 'am' | 'pm' | null): number | null {
  if (minute > 59) return null;
  let h = hour;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    h = hour === 12 ? 0 : hour;
    if (meridiem === 'pm') h += 12;
  } else if (hour > 23) {
    return null;
  }
  return h * 60 + minute;
}

/* Phrases must sit at word boundaries, or "summon" would surrender its "mon".
   The check is done in CODE rather than with a lookbehind assertion: Hermes —
   the engine the app actually runs on — has historically not supported
   lookbehind, and a regex that throws at parse time on device while passing in
   Node is the worst possible way to learn that. `parse.ts` checks its boundaries
   the same way, for the same reason. */
const BOUNDARY = /[\s(["'\u2018\u201c]/;
const BOUNDARY_AFTER = /[\s).,;:!?\]"'\u2019\u201d]/;

/** Is the character before `i` a boundary (or is `i` the start of the text)? */
function boundaryBefore(text: string, i: number): boolean {
  return i === 0 || BOUNDARY.test(text[i - 1]!);
}

/** Is the character at `i` a boundary (or is `i` the end of the text)? */
function boundaryAfter(text: string, i: number): boolean {
  return i >= text.length || BOUNDARY_AFTER.test(text[i]!);
}

function anchored(body: string): RegExp {
  return new RegExp('(?:' + body + ')', 'gi');
}

/* `sat` and `sun` are missing on purpose. Every other weekday abbreviation is
   unmistakably a weekday, but those two are ordinary English words ("I sat
   down", "sun cream") and a task name is exactly where they'd appear. The full
   names still parse, so nothing is unreachable — you just have to write
   "saturday". This is the one place the vocabulary is trimmed for false
   positives rather than for ambiguity. */
const NO_ABBREV = new Set(['saturday', 'sunday']);
const WEEKDAY_ALT = WEEKDAYS.map((d) => (NO_ABBREV.has(d) ? d : `${d}|${d.slice(0, 3)}`)).join('|');
const MONTH_ALT = MONTHS.map((m) => `${m}|${m.slice(0, 3)}`).join('|');
const COUNT_ALT = Object.keys(NUMBER_WORDS).join('|');

/** What an "every …" phrase resolved to: the cadence, and the day the chain
    should start on. */
interface RecurHit {
  preset: RecurrencePreset;
  day: string;
  /** True when a weekly cadence took its weekday from the ANCHOR ("every week")
      rather than from the phrase itself ("every monday"). Only the first kind
      follows an explicit day typed alongside it — see `parseCaptureDate`. */
  bydayFromAnchor?: boolean;
}

/** The weekday a cadence should ANCHOR on. Unlike a one-off date, today counts:
    "every monday" typed on a Monday means the series includes today, where a
    bare "monday" means the one coming (someone naming a single day and meaning
    today would have typed "today" — a cadence has no such alternative). */
function anchorWeekday(today: string, weekday: number): string {
  const delta = (weekday - dayOfWeek(today) + 7) % 7;
  return addDaysYMD(today, delta);
}

/**
 * Every recurrence phrase, most specific first.
 *
 * These are scanned BEFORE the one-off day patterns, which is what stops "every
 * monday" from being read as the bare weekday "monday" with a stranded "every"
 * left in the task name — the bug this list was added to fix.
 *
 * The vocabulary is bounded by `RecurrencePreset` (daily / weekly+byday /
 * monthly, each with an interval), which is the same set the Repeat chip offers.
 * A phrase with no preset to land in — "every year" — is deliberately absent
 * rather than approximated.
 */
const RECUR_PATTERNS: { re: RegExp; resolve: (m: RegExpExecArray, ctx: Ctx) => RecurHit | null }[] = [
  {
    // "every weekday" — Mon–Fri, anchored on the next one that qualifies
    re: anchored('every\\s+weekday(?:s)?'),
    resolve: (_m, ctx) => {
      let day = ctx.today;
      while (dayOfWeek(day) === 0 || dayOfWeek(day) === 6) day = addDaysYMD(day, 1);
      return { preset: { freq: 'weekly', interval: 1, byday: ['MO', 'TU', 'WE', 'TH', 'FR'] }, day };
    },
  },
  {
    // "every monday" — weekly on that weekday
    re: anchored(`every\\s+(${WEEKDAY_ALT})`),
    resolve: (m, ctx) => {
      const wd = weekdayIndex(m[1]!);
      return {
        preset: { freq: 'weekly', interval: 1, byday: [WEEKDAY_CODE[wd]!] },
        day: anchorWeekday(ctx.today, wd),
      };
    },
  },
  {
    // "every other day" / "every other week" / "every other month"
    re: anchored('every\\s+other\\s+(day|week|month)'),
    resolve: (m, ctx) => recurEvery(m[1]!.toLowerCase(), 2, ctx),
  },
  {
    // "every 3 days" / "every two weeks" / "every 6 months"
    re: anchored(`every\\s+(\\d{1,3}|${COUNT_ALT})\\s+(days|day|weeks|week|months|month)`),
    resolve: (m, ctx) => {
      const n = NUMBER_WORDS[m[1]!.toLowerCase()] ?? Number(m[1]);
      if (!Number.isFinite(n) || n < 1) return null;
      return recurEvery(m[2]!.toLowerCase().replace(/s$/, ''), n, ctx);
    },
  },
  {
    // "every day" / "daily", "every week" / "weekly", "every month" / "monthly"
    re: anchored('every\\s+(day|week|month)|daily|weekly|monthly'),
    resolve: (m, ctx) => {
      const unit = (m[1] ?? m[0]!.toLowerCase().replace(/ly$/, '')).toLowerCase();
      // "daily" → "dai", "weekly" → "week", "monthly" → "month"
      return recurEvery(unit.startsWith('dai') ? 'day' : unit, 1, ctx);
    },
  },
];

/** A unit + interval as a preset anchored on today. Weekly takes today's own
    weekday, which is what the Repeat chip's "Every week" does from the day the
    task sits on. */
function recurEvery(unit: string, interval: number, ctx: Ctx): RecurHit | null {
  if (unit === 'day') return { preset: { freq: 'daily', interval }, day: ctx.today };
  if (unit === 'week') {
    return {
      preset: { freq: 'weekly', interval, byday: [WEEKDAY_CODE[dayOfWeek(ctx.today)]!] },
      day: ctx.today,
      bydayFromAnchor: true,
    };
  }
  if (unit === 'month') return { preset: { freq: 'monthly', interval }, day: ctx.today };
  return null;
}

/** Every day-phrase pattern, most specific FIRST — "next monday" has to win over
    the "monday" sitting inside it, and the scanner takes the first pattern that
    claims a region. */
const DAY_PATTERNS: { re: RegExp; resolve: (m: RegExpExecArray, ctx: Ctx) => string | null }[] = [
  {
    // ISO is unambiguous by construction, which is the only reason a numeric
    // date form is here at all (see the header note on 12/25)
    re: anchored('\\d{4}-\\d{2}-\\d{2}'),
    resolve: (m) => {
      const ymd = m[0]!;
      // reject 2026-02-30 and friends by round-tripping
      return utcToYMD(ymdToUTC(ymd)) === ymd ? ymd : null;
    },
  },
  {
    re: anchored(`next\\s+(${WEEKDAY_ALT})`),
    resolve: (m, ctx) =>
      weekdayInNextWeek(ctx.today, weekdayIndex(m[1]!), ctx.weekStartsOn),
  },
  {
    // "next month" / "in 2 months" — the SAME day of the month, clamped. Both
    // readings ("the same date" vs "the first") are in circulation; this is the
    // one Todoist, TickTick and every date library use, and `sep 3` says the
    // other exactly. Clamping means the 31st lands on the 30th, not in March.
    re: anchored(`in\\s+(\\d{1,3}|${COUNT_ALT})\\s+(months|month)`),
    resolve: (m, ctx) => {
      const n = NUMBER_WORDS[m[1]!.toLowerCase()] ?? Number(m[1]);
      if (!Number.isFinite(n) || n < 1) return null;
      return addMonthsYMD(ctx.today, n);
    },
  },
  {
    re: anchored('next\\s+month'),
    resolve: (_m, ctx) => addMonthsYMD(ctx.today, 1),
  },
  {
    re: anchored('next\\s+week'),
    resolve: (_m, ctx) => addDaysYMD(weekStartOf(ctx.today, ctx.weekStartsOn), 7),
  },
  {
    // "next weekend" needs its own entry, ahead of both `next week` and the bare
    // `weekend` below. Without it `next\\s+week` matches the "next week" sitting
    // inside "next weekend", fails its trailing boundary on the "e", and the
    // bare pattern then claims only "weekend" — resolving to THIS Saturday and
    // orphaning the word "next" in the task name. Read like `next <weekday>`:
    // the Saturday of the following week.
    re: anchored('next\\s+weekend'),
    resolve: (_m, ctx) => weekdayInNextWeek(ctx.today, 6, ctx.weekStartsOn),
  },
  {
    // "this weekend" and a bare "weekend" both mean the coming Saturday — the
    // day the weekend starts, not the two-day range, because a task lands on one
    // day. Saturday itself resolves forward like any other weekday.
    re: anchored('(?:this\\s+)?weekend'),
    resolve: (_m, ctx) => nextWeekday(ctx.today, 6),
  },
  {
    // Alternatives run LONGEST-FIRST here and everywhere else in this file:
    // the boundary check happens outside the regex (see `boundaryAfter`), so it
    // cannot make the engine backtrack. `(day|days)` would match "day" inside
    // "days", fail the boundary on the trailing "s", and drop the phrase.
    re: anchored(`in\\s+(\\d{1,3}|${COUNT_ALT})\\s+(days|day|weeks|week)`),
    resolve: (m, ctx) => {
      const n = NUMBER_WORDS[m[1]!.toLowerCase()] ?? Number(m[1]);
      if (!Number.isFinite(n) || n < 1) return null;
      return addDaysYMD(ctx.today, m[2]!.toLowerCase().startsWith('week') ? n * 7 : n);
    },
  },
  {
    // "tonight" is today. It is NOT given an implied evening time: inventing
    // 20:00 would be exactly the silent guess this parser exists to avoid, and
    // the user who wants one types "tonight 8pm".
    re: anchored('today|tonight'),
    resolve: (_m, ctx) => ctx.today,
  },
  {
    re: anchored('tomorrow|tmrw|tmr'),
    resolve: (_m, ctx) => addDaysYMD(ctx.today, 1),
  },
  {
    // "aug 9" / "august 9th"
    re: anchored(`(${MONTH_ALT})\\s+(\\d{1,2})(?:st|nd|rd|th)?`),
    resolve: (m, ctx) => monthDayToYMD(ctx.today, monthIndex(m[1]!), Number(m[2])),
  },
  {
    // "9 aug" / "9th august" — the same date the other way round, which is how
    // most of the world writes it
    re: anchored(`(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_ALT})`),
    resolve: (m, ctx) => monthDayToYMD(ctx.today, monthIndex(m[2]!), Number(m[1])),
  },
  {
    re: anchored(`(${WEEKDAY_ALT})`),
    resolve: (m, ctx) => nextWeekday(ctx.today, weekdayIndex(m[1]!)),
  },
];

/** Every time-phrase pattern, most specific first. */
const TIME_PATTERNS: { re: RegExp; resolve: (m: RegExpExecArray) => number | null }[] = [
  {
    // "at 5:30 pm" / "5:30pm" / "17:00" — a colon or a meridiem, never a bare
    // hour (see the header note)
    /* The space before the meridiem lives INSIDE the optional group. Written as
       `\\s*(am|pm)?` the `\\s*` still eats the trailing space when no meridiem
       follows, so `boundaryAfter` is then tested against the first letter of the
       next word and rejects the whole match — "meet 17:00 tomorrow" silently
       lost its time, and `lastIndex` had already moved past it so no shorter
       match was retried. */
    re: anchored('(?:at\\s+)?(\\d{1,2}):(\\d{2})(?:\\s*(am|pm))?'),
    resolve: (m) =>
      toMinutes(Number(m[1]), Number(m[2]), (m[3]?.toLowerCase() as 'am' | 'pm') ?? null),
  },
  {
    // "at 5pm" / "5 pm"
    re: anchored('(?:at\\s+)?(\\d{1,2})\\s*(am|pm)'),
    resolve: (m) => toMinutes(Number(m[1]), 0, m[2]!.toLowerCase() as 'am' | 'pm'),
  },
  {
    re: anchored('noon'),
    resolve: () => 12 * 60,
  },
];

interface Ctx {
  today: string;
  weekStartsOn: WeekStartDay;
}

function weekdayIndex(word: string): number {
  const w = word.toLowerCase();
  return WEEKDAYS.findIndex((d) => d === w || d.slice(0, 3) === w);
}

function monthIndex(word: string): number {
  const w = word.toLowerCase();
  return MONTHS.findIndex((m) => m === w || m.slice(0, 3) === w);
}

/** Does `span` overlap anything already claimed? First claim wins, which is what
    makes the most-specific-first ordering of the pattern lists meaningful. */
function overlaps(taken: DateSpan[], start: number, end: number): boolean {
  return taken.some((s) => start < s.end && end > s.start);
}

/**
 * Run one pattern list over the text and claim the winning region.
 *
 * One day and one time per capture — a task has exactly one of each — so this
 * picks a single match. The winner is the **leftmost**, and only then the
 * longest, and only then the earliest pattern in the list.
 *
 * Leftmost matters, and taking the first pattern that matched anywhere (the
 * original shape of this) was wrong: with the patterns ordered most-specific
 * first, "pay rent monday buy milk tomorrow" resolved to *tomorrow* because
 * `tomorrow` sits above the bare-weekday pattern — leaving "monday" stranded in
 * the saved name. Collecting the candidates and choosing by position is what
 * makes "the earliest phrase is the one the sentence was built around" true
 * rather than aspirational.
 *
 * Longest-at-the-same-start is what still lets "next monday" beat the "monday"
 * inside it, which is the job the pattern ordering used to do alone.
 */
function scan<T>(
  text: string,
  taken: DateSpan[],
  patterns: { re: RegExp; resolve: (m: RegExpExecArray, ctx: Ctx) => T | null }[],
  ctx: Ctx,
  put: (value: T) => void,
): void {
  /* A plain indexed loop rather than `forEach`: TypeScript's control-flow
     analysis doesn't track assignments made inside a callback, so `best` would
     narrow to `null` (and then to `never`) after the loop. */
  let best: { start: number; end: number; value: T; rank: number } | null = null;

  for (let rank = 0; rank < patterns.length; rank += 1) {
    const { re, resolve } = patterns[rank]!;
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const start = m.index;
      const end = start + m[0]!.length;
      // a zero-width match would spin forever; regexes here can't produce one,
      // but the guard costs nothing next to a hung keystroke handler
      if (end === start) {
        re.lastIndex += 1;
        continue;
      }
      // a candidate that can't beat the incumbent on position isn't worth
      // resolving — and once past it, nothing later in this pattern can either
      if (best && start > best.start) break;
      if (!boundaryBefore(text, start) || !boundaryAfter(text, end)) continue;
      if (overlaps(taken, start, end)) continue;
      const value = resolve(m, ctx);
      if (value === null) continue;
      const better =
        !best ||
        start < best.start ||
        (start === best.start && (end > best.end || (end === best.end && rank < best.rank)));
      if (better) best = { start, end, value, rank };
    }
  }

  if (!best) return;
  taken.push({ start: best.start, end: best.end });
  put(best.value);
}

/**
 * Read a date and/or a time out of quick-add text.
 *
 * Returns null when nothing matched — the common case for ordinary task names,
 * and what tells the caller to leave the date chips alone.
 *
 * `today` is the caller's local day ("YYYY-MM-DD"); nothing here reads a clock.
 */
export function parseCaptureDate(
  text: string,
  today: string,
  weekStartsOn: WeekStartDay = 0,
): CaptureDate | null {
  const ctx: Ctx = { today, weekStartsOn };
  const taken: DateSpan[] = [];
  let day: string | null = null;
  let remindAt: number | null = null;
  let recurrence: RecurrencePreset | null = null;
  let recurrenceDay: string | null = null;

  /* RECURRENCE FIRST. "every monday" has to claim the whole phrase before the
     bare-weekday pattern can take the "monday" out of the middle of it and leave
     "every" stranded in the task name. */
  let bydayFromAnchor = false;
  scan<RecurHit>(text, taken, RECUR_PATTERNS, ctx, (value) => {
    recurrence = value.preset;
    recurrenceDay = value.day;
    bydayFromAnchor = value.bydayFromAnchor ?? false;
  });
  scan<string>(text, taken, DAY_PATTERNS, ctx, (value) => {
    day = value;
  });
  scan<number>(text, taken, TIME_PATTERNS, ctx, (value) => {
    remindAt = value;
  });

  /* An explicit day OUTRANKS the cadence's own anchor, so "every week starting
     tomorrow" begins tomorrow rather than today. Otherwise the cadence supplies
     one, because a repeating task must sit on a day to repeat from. */
  if (day === null && recurrenceDay !== null) day = recurrenceDay;
  /* A weekly cadence that took its weekday from the anchor has to FOLLOW an
     explicit day typed alongside it — "every week starting tomorrow" repeats on
     tomorrow's weekday, not today's. "every monday" named its weekday outright
     and keeps it. */
  if (
    bydayFromAnchor &&
    day !== null &&
    recurrence !== null &&
    (recurrence as RecurrencePreset).freq === 'weekly'
  ) {
    const weekly = recurrence as Extract<RecurrencePreset, { freq: 'weekly' }>;
    recurrence = { ...weekly, byday: [WEEKDAY_CODE[dayOfWeek(day)]!] };
  }

  if (day === null && remindAt === null && recurrence === null) return null;

  const spans = [...taken].sort((a, b) => a.start - b.start);
  return {
    day,
    remindAt,
    recurrence,
    spans,
    phrase: spans.map((s) => text.slice(s.start, s.end)).join(' '),
  };
}

/** Overlapping spans merged into their unions, in order. Slicing a string by
    spans that overlap removes the shared characters twice and takes innocent
    text with them, so every consumer of a MIXED span list (dates from here,
    token cuts from parse.ts) must merge first. */
export function mergeSpans(spans: readonly DateSpan[]): DateSpan[] {
  const sorted = [...spans].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: DateSpan[] = [];
  for (const span of sorted) {
    const last = out[out.length - 1];
    if (last && span.start <= last.end) last.end = Math.max(last.end, span.end);
    else out.push({ ...span });
  }
  return out;
}

/** Cut every parsed span out of the text, tidying the whitespace they leave —
    the date half of what `spliceToken` does for a `#project` token. Spans are
    merged first, so a caller may pass a mixed list without auditing it. */
export function stripDateSpans(text: string, spans: readonly DateSpan[]): string {
  let out = text;
  for (const span of mergeSpans(spans).reverse()) {
    out = out.slice(0, span.start) + out.slice(span.end);
  }
  return out.replace(/\s+/g, ' ').trim();
}

/**
 * Blank out `spans` with spaces, keeping every other character at its own index.
 *
 * This is how another axis claims characters BEFORE the date parser sees them.
 * A `#` token can cover several words (`matchProjectName` reads "Camp August" as
 * one project name), and a date word inside that run is still preceded by a
 * space — so it passes `boundaryBefore` and gets read as a date. "Pack #Camp
 * August 9 gear" produced a project span and an overlapping date span, and the
 * name came out as "Pack": "gear" was collateral damage of slicing two spans
 * that shared characters.
 *
 * Masking settles it by precedence instead of by repair: the sigil'd axis owns
 * its characters, the date parser never sees inside them, and the spans it
 * returns still index into the original text because the length is preserved.
 */
export function maskSpans(text: string, spans: readonly DateSpan[]): string {
  if (spans.length === 0) return text;
  const chars = [...text];
  for (const span of mergeSpans(spans)) {
    for (let i = span.start; i < span.end && i < chars.length; i += 1) chars[i] = ' ';
  }
  return chars.join('');
}
